/**
 * Virtualized listbox components for MUI Autocomplete dropdowns.
 *
 * ## What is virtualization?
 *
 * **List virtualization** (also called "windowing") renders only the rows currently
 * visible in the scroll viewport (plus a small overscan buffer), instead of mounting
 * every option in the DOM. For a dropdown with thousands of items, a non-virtualized
 * list creates thousands of nodes and can freeze the main thread when the panel opens.
 * A virtualized list keeps DOM size roughly constant regardless of total item count.
 *
 * ## Official recommendation
 *
 * MUI documents this pattern under Autocomplete → **Virtualization**: when option
 * counts are large, supply a custom `ListboxComponent` backed by `react-window`.
 * MUI's own demo virtualizes a 10,000-option list this way. The `createFilterOptions`
 * docs also note that `limit` is mainly a fallback when virtualization is not used.
 *
 * @see {@link https://mui.com/material-ui/react-autocomplete/#virtualization MUI Autocomplete — Virtualization}
 * @see {@link https://react-window.vercel.app/ react-window documentation}
 * @see {@link https://github.com/bvaughn/react-window react-window (GitHub)}
 */
import React from 'react';
import { VariableSizeList } from 'react-window';

/** Vertical padding applied inside the virtualized list container. */
const LISTBOX_PADDING = 8;

/**
 * Renders a single virtualized row by cloning the Autocomplete option element
 * and offsetting its top position to account for list padding.
 * @param {Object} props - react-window row props
 * @param {Array<React.ReactElement>} props.data - Option elements from Autocomplete
 * @param {number} props.index - Row index
 * @param {Object} props.style - Positioning styles from react-window
 * @returns {React.ReactElement}
 */
function renderRow(props) {
    const { data, index, style } = props;
    return React.cloneElement(data[index], {
        style: {
            ...style,
            top: style.top + LISTBOX_PADDING,
        },
    });
}

/** Passes Autocomplete listbox props (role, aria, etc.) to the scroll container. */
const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

/**
 * Resets react-window item size cache when the option count changes.
 * @param {number} data - Number of list items (used as effect dependency)
 * @returns {React.MutableRefObject<import('react-window').VariableSizeList|null>}
 */
function useResetCache(data) {
    const ref = React.useRef(null);
    React.useEffect(() => {
        if (ref.current !== null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);
    return ref;
}

/**
 * Factory for a virtualized MUI Autocomplete `ListboxComponent`.
 *
 * Implements MUI's recommended approach: replace the default `<ul>` listbox with a
 * `react-window` `VariableSizeList` so Autocomplete only mounts visible option rows.
 * Pass the returned component to Autocomplete's `ListboxComponent` prop (or use the
 * pre-built {@link VirtualizedListbox} / {@link VirtualizedListboxLarge} exports).
 *
 * @param {Object} [config]
 * @param {number} [config.itemSize=48] - Fixed row height in px (must match rendered option height)
 * @param {number} [config.maxVisibleItems=8] - Max rows shown before the list scrolls
 * @returns {React.ForwardRefExoticComponent} ListboxComponent compatible with MUI Autocomplete
 * @see {@link https://mui.com/material-ui/react-autocomplete/#virtualization MUI Autocomplete — Virtualization}
 * @see {@link https://react-window.vercel.app/#/examples/list/variable-size react-window VariableSizeList}
 */
export function createVirtualizedListbox({ itemSize = 48, maxVisibleItems = 8 } = {}) {
    return React.forwardRef(function VirtualizedListbox(props, ref) {
        const { children, ...other } = props;
        const itemData = React.Children.toArray(children);
        const itemCount = itemData.length;

        const getChildSize = () => itemSize;

        const getHeight = () => {
            if (itemCount > maxVisibleItems) {
                return maxVisibleItems * itemSize;
            }
            return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
        };

        const gridRef = useResetCache(itemCount);

        return (
            <div ref={ref}>
                <OuterElementContext.Provider value={other}>
                    <VariableSizeList
                        itemData={itemData}
                        height={getHeight() + 2 * LISTBOX_PADDING}
                        width="100%"
                        ref={gridRef}
                        outerElementType={OuterElementType}
                        innerElementType="ul"
                        itemSize={() => itemSize}
                        overscanCount={5}
                        itemCount={itemCount}
                    >
                        {renderRow}
                    </VariableSizeList>
                </OuterElementContext.Provider>
            </div>
        );
    });
}

/**
 * Virtualized listbox for single-line Autocomplete options (e.g. {@link MultiSelect}).
 *
 * Uses a 48px row height. Prefer this over the default Autocomplete listbox when
 * `options.length` is in the hundreds or thousands.
 *
 * @see {@link https://mui.com/material-ui/react-autocomplete/#virtualization MUI Autocomplete — Virtualization}
 */
export const VirtualizedListbox = createVirtualizedListbox({ itemSize: 48 });

/**
 * Virtualized listbox for two-line Autocomplete options (primary + secondary text).
 *
 * Uses an 88px row height to fit name + description rows (e.g. search results).
 *
 * @see {@link https://mui.com/material-ui/react-autocomplete/#virtualization MUI Autocomplete — Virtualization}
 */
export const VirtualizedListboxLarge = createVirtualizedListbox({ itemSize: 88 });
